import type { FinsterQueryDataSource } from "../shared/api/finster/constants";
import type { ExcelTabsState } from "./types";
import {
  resolveSessionDataSources,
  snapshotRunResearchSelection,
  type ResearchSelection,
} from "../shared/researchSelection";

interface ExcelSessionResearchSelectionInput {
  selectedDataSources?: readonly FinsterQueryDataSource[];
  selectedUserFileIds?: readonly string[];
  researchSelectionMode?: ExcelResearchSelectionMode;
}

export {
  normalizeSelectedUserFileIds,
  resolveSessionDataSources as resolveExcelSessionDataSources,
  selectUserFiles as selectExcelUserFiles,
  selectDataSources as selectExcelDataSources,
  snapshotRunResearchSelection as snapshotExcelRunResearchSelection,
} from "../shared/researchSelection";
export type ExcelResearchSelection = ResearchSelection;
export type ExcelResearchSelectionMode = "default" | "filtered" | "user_files";

export interface ExcelSubmittedResearchSelection extends ExcelResearchSelection {
  selectionMode: ExcelResearchSelectionMode;
}

/**
 * Live composer mode after a file-id change. Unset tabs are still All-on
 * (`default`); only an explicit prior filter stays `filtered`. Clearing
 * files from a files-only tab keeps the empty source list as `filtered`
 * so submit does not fall back to automatic sources.
 */
export function resolveExcelLiveResearchSelectionMode(
  selection: ResearchSelection,
  previousMode?: ExcelResearchSelectionMode,
): ExcelResearchSelectionMode {
  if (selection.userFileIds.length > 0 && selection.dataSources.length === 0) {
    return "user_files";
  }
  if (selection.dataSources.length === 0) {
    return "filtered";
  }
  return previousMode === "filtered" ? "filtered" : "default";
}

/**
 * Captures the research scope visible on the submitted tab. Call this before
 * resolving workbook identity so hydration cannot replace a user's live source
 * or file selection while the host lookup is in flight.
 */
export function captureExcelSubmittedResearchSelection(
  availableDataSources: readonly FinsterQueryDataSource[],
  session: ExcelSessionResearchSelectionInput,
): ExcelSubmittedResearchSelection {
  const selection = snapshotRunResearchSelection({
    dataSources: resolveSessionDataSources(
      availableDataSources,
      Array.isArray(session.selectedDataSources)
        ? session.selectedDataSources
        : undefined,
    ),
    userFileIds: session.selectedUserFileIds,
  });
  return {
    ...selection,
    selectionMode:
      selection.userFileIds.length > 0 && selection.dataSources.length === 0
        ? "user_files"
        : session.researchSelectionMode === "default"
          ? "default"
          : Array.isArray(session.selectedDataSources)
            ? "filtered"
            : "default",
  };
}

/**
 * Persists a captured submit-time scope onto the active tab chosen for the run.
 * This keeps the resolved workbook slot, retries, and the executor aligned
 * without replacing loaded chat history.
 */
export function applyExcelResearchSelectionToActiveTab(
  tabsState: ExcelTabsState,
  selection: ExcelSubmittedResearchSelection,
  updatedAt = Date.now(),
): ExcelTabsState {
  return {
    ...tabsState,
    tabs: tabsState.tabs.map((tab) =>
      tab.id === tabsState.activeTabId
        ? {
            ...tab,
            session: {
              ...tab.session,
              selectedDataSources: [...selection.dataSources],
              selectedUserFileIds: [...selection.userFileIds],
              researchSelectionMode: selection.selectionMode,
              updatedAt,
            },
          }
        : tab,
    ),
  };
}

export function shouldDeferExcelRunAfterWorkbookSwitch(
  visibleWorkbookKey: string,
  resolvedWorkbookKey: string,
  isPausedPlanRetry: boolean,
): boolean {
  return (
    !isPausedPlanRetry &&
    visibleWorkbookKey.length > 0 &&
    resolvedWorkbookKey !== visibleWorkbookKey &&
    resolvedWorkbookKey.length > 0
  );
}
